import { HttpStatusCode, isAxiosError } from 'axios';

export interface ProblemDetail {
    type: string;
    title: string;
    status: HttpStatusCode;
    detail: string;
    instance: string;
    errorCode: string;
}

export function isProblemDetail(errorResponse: unknown): errorResponse is ProblemDetail {
    return (
        typeof errorResponse === 'object' &&
        errorResponse !== null &&
        'type' in errorResponse &&
        'title' in errorResponse &&
        'detail' in errorResponse
    );
}

export function isProblemDetailTextOr(errorResponse: unknown, elseText: string): string {
    if (isProblemDetail(errorResponse)) {
        return errorResponse.detail;
    }
    return elseText;
}

export function isProblemDetailCode<T extends ProblemDetail>(errorResponse: unknown, code: string): errorResponse is T {
    return isProblemDetail(errorResponse) && errorResponse.errorCode === code;
}

/**
 * HTTP status of a rejected request, whichever shape the axios layer produced.
 *
 * The response interceptor in `http-instance.ts` rejects with the bare
 * ProblemDetail body for every status except 401/403, so most HTTP errors do
 * not arrive as an AxiosError at all and `err.response.status` is unreachable.
 * Network failures (no response) still reject with the AxiosError. Callers that
 * branch on the status must handle both shapes.
 *
 * Any numeric `status` on the rejected body counts, rather than only a body
 * passing `isProblemDetail`: Spring omits `detail` when it is null, so a
 * problem detail can arrive as `{type, title, status, instance}` and must
 * still yield its status.
 */
export function httpStatusOf(errorResponse: unknown): number | undefined {
    if (typeof errorResponse === 'object' && errorResponse !== null && 'status' in errorResponse) {
        const { status } = errorResponse as { status: unknown };
        if (typeof status === 'number') {
            return status;
        }
    }
    if (isAxiosError(errorResponse)) {
        return errorResponse.response?.status;
    }
    return undefined;
}
